import {
  type AffixId,
  affixCountOf,
  getItem,
  type InventoryState,
  isAffixable,
  RARITY_COLORS,
  reforgeCost,
  type ReforgeRejected,
  type ReforgeResult,
} from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import {
  benchHead,
  firstShortfall,
  formatGold,
  goldNeed,
  type Need,
  needIcon,
  needList,
  purseChip,
  rarityHex,
  section,
  setPurse,
  workAction,
} from './bench.js';
import { fillPieceList, type Piece, piecesOf } from './craft-pieces.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { kitTitle } from './kit.js';
import { beforeAfter, possibleRunes, type RuneChoice, runeRow } from './runesmith-runes.js';
import { playUpgradeSuccess } from './sfx.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './runesmith.css';

/**
 * The runesmith's window (ADR 0057): pick a piece that carries affixes,
 * choose for each rune to keep it, redraw its value, draw another or seal it
 * for good, see the price, and ask. The choices stay from one reroll to the
 * next, so the same reroll can be asked again at once. It borrows the smith's
 * frame and layout (smith.css). Only the intent leaves; the new affixes are
 * the server's, shown before and after once it answers.
 */

export interface RunesmithPanelOptions {
  icons: CssAtlas | null;
  onReforge: (
    itemId: string,
    choices: { locks: AffixId[]; values: AffixId[]; seals: AffixId[] },
  ) => void;
}

const REFUSALS: Record<ReforgeRejected['reason'], string> = {
  invalid: 'Demande invalide.',
  'unknown-item': 'Tu ne possèdes plus cette pièce.',
  'not-affixable': 'Le graveur ne peut rien graver sur cet objet.',
  'all-locked': 'Laisse au moins une rune à regraver.',
  'unknown-lock': 'Cette rune n’est plus sur la pièce.',
  sealed: 'Cette rune est scellée : on ne peut plus y toucher.',
  'missing-materials': 'Il te manque des matériaux.',
  'missing-gold': 'Il te manque de l’or.',
  'too-far': 'Approche-toi du graveur.',
  'rate-limited': 'Le graveur souffle un instant.',
  closed: 'Le graveur ne travaille pas tant que le village est attaqué.',
};

export class RunesmithPanel {
  private readonly root: HTMLElement;
  private readonly purse: HTMLElement;
  private readonly list: HTMLElement;
  private readonly detail: HTMLElement;
  /** Warnings and the button: always in view under the scrolling columns. */
  private readonly footer: HTMLElement;
  private readonly message: HTMLElement;
  private inventory: InventoryState | undefined;
  private selectedId: string | undefined;
  /** Every rune not named here is drawn anew. */
  private choices = new Map<AffixId, Exclude<RuneChoice, 'new'>>();
  /** Whether the list of possible runes is unfolded; kept across redraws. */
  private possibleOpen = false;
  /** The last reroll of the selected piece, shown until another piece is picked. */
  private last: ReforgeResult | undefined;
  private inReach = false;
  private pending = false;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: RunesmithPanelOptions) {
    this.root = document.createElement('section');
    this.root.className = 'smith runesmith';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Graveur de runes'));

    const title = document.createElement('h2');
    title.className = 'smith__title';
    kitTitle(title, 'stat-upgrade');
    title.textContent = uiText('Graveur de runes');
    title.append(
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );

    this.purse = purseChip();
    const head = benchHead(
      uiText('Pour chaque rune : la garder, changer sa valeur, en tirer une autre ou la sceller.'),
      this.purse,
    );
    this.list = document.createElement('div');
    this.list.className = 'smith__list';
    this.detail = document.createElement('div');
    this.detail.className = 'smith__detail runesmith__detail';
    this.footer = document.createElement('div');
    this.footer.className = 'runesmith__footer';
    this.message = document.createElement('p');
    this.message.className = 'smith__message';
    this.message.setAttribute('aria-live', 'polite');

    const body = document.createElement('div');
    body.className = 'smith__body runesmith__body';
    this.list.classList.add('runesmith__list');
    body.append(this.list, this.detail);
    this.root.append(title, head, body, this.footer, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  open(): void {
    this.root.hidden = false;
    this.message.textContent = '';
    this.render();
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
  }

  setInventory(state: InventoryState): void {
    this.inventory = state;
    this.render();
  }

  setInReach(inReach: boolean): void {
    if (inReach === this.inReach) return;
    this.inReach = inReach;
    this.render();
  }

  showResult(result: ReforgeResult): void {
    this.pending = false;
    this.last = result;
    // The choices stay for the next reroll; only a rune just sealed, or one
    // no longer on the piece, leaves them.
    for (const [id] of this.choices) {
      const affix = result.affixes.find((entry) => entry.id === id);
      if (affix === undefined || affix.sealed === true) this.choices.delete(id);
    }
    const name = uiText(getItem(result.templateId)?.name ?? result.templateId);
    this.say(uiFormat('Les runes de {name} sont regravées.', { name }), 'success');
    playUpgradeSuccess();
    this.render();
  }

  showRejected(rejection: ReforgeRejected): void {
    this.pending = false;
    this.say(REFUSALS[rejection.reason], 'refused');
    this.render();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }

  private say(text: string, tone: 'success' | 'refused'): void {
    this.message.textContent = uiText(text);
    this.message.dataset.tone = tone;
    this.root.classList.remove('smith--success');
    if (tone === 'refused') return;
    this.root.getBoundingClientRect();
    this.root.classList.add('smith--success');
  }

  /** Worn pieces first, then the bag: only what carries affixes. */
  private pieces(): Piece[] {
    return piecesOf(
      this.inventory,
      (instance, template) => isAffixable(template) && (instance.affixes?.length ?? 0) > 0,
    );
  }

  private owned(templateId: string): number {
    return (this.inventory?.bag ?? [])
      .filter((entry) => entry.item.templateId === templateId)
      .reduce((sum, entry) => sum + entry.item.quantity, 0);
  }

  private select(id: string | undefined): void {
    if (id === this.selectedId) return;
    this.selectedId = id;
    this.choices = new Map();
    this.last = undefined;
  }

  private render(): void {
    if (this.root.hidden) return;
    setPurse(this.purse, this.inventory?.gold ?? 0);
    const pieces = this.pieces();
    if (!pieces.some((piece) => piece.instance.id === this.selectedId)) {
      this.select(pieces[0]?.instance.id);
    }

    fillPieceList(this.list, pieces, {
      icons: this.options.icons,
      selectedId: this.selectedId,
      empty: uiText(
        'Rien à graver : apporte une pièce rare, épique ou légendaire trouvée en chassant.',
      ),
      onPick: (piece) => {
        this.select(piece.instance.id);
        this.message.textContent = '';
        this.render();
      },
    });
    this.renderDetail(pieces.find((piece) => piece.instance.id === this.selectedId));
  }

  private renderDetail(piece: Piece | undefined): void {
    hideItemHover();
    this.detail.replaceChildren();
    this.footer.replaceChildren();
    if (piece === undefined) return;
    const template = getItem(piece.instance.templateId);
    if (template === undefined) return;
    const affixes = piece.instance.affixes ?? [];

    const level = piece.instance.upgradeLevel;
    const name = document.createElement('p');
    name.className = 'smith__name';
    name.style.color = rarityHex(RARITY_COLORS[template.rarity]);
    name.textContent =
      level > 0 ? `${uiText(template.name)} +${String(level)}` : uiText(template.name);
    this.detail.append(name);

    // What stays whole (sealed before, kept or sealed now) is below the
    // count: the reroll must change something.
    const keepable =
      affixCountOf(template) - 1 - affixes.filter((affix) => affix.sealed === true).length;
    const whole = this.named('keep').length + this.named('seal').length;
    const runes = document.createElement('div');
    runes.className = 'runesmith__runes';
    for (const affix of affixes) {
      runes.append(
        runeRow({
          affix,
          template,
          current: this.choices.get(affix.id) ?? 'new',
          wholeFull: whole >= keepable,
          onChoose: (choice) => {
            if (choice === 'new') this.choices.delete(affix.id);
            else this.choices.set(affix.id, choice);
            // A new reroll is being chosen: the last one's before and after,
            // left beside the new cost, would read as a preview of it.
            this.last = undefined;
            this.message.textContent = '';
            this.render();
          },
        }),
      );
    }
    this.detail.append(
      runes,
      possibleRunes(template, this.possibleOpen, (open) => {
        this.possibleOpen = open;
      }),
    );

    const locks = this.named('keep');
    const values = this.named('value');
    const seals = this.named('seal');
    const cost = reforgeCost(template, locks.length + values.length, seals.length);
    if (cost === undefined) return;
    const needs: Need[] = cost.materials.map((material) => {
      const item = getItem(material.templateId);
      return {
        icon: needIcon(item, material.templateId, this.options.icons),
        name: uiText(item?.name ?? material.templateId),
        have: this.owned(material.templateId),
        need: material.quantity,
      };
    });
    needs.push(goldNeed(cost.gold, this.inventory?.gold ?? 0));
    this.detail.append(section(uiText('Coût de la gravure')), needList(needs));

    if (seals.length > 0) {
      const warn = document.createElement('p');
      warn.className = 'smith__warn runesmith__warn';
      warn.textContent = uiText('Une rune scellée ne pourra plus jamais être changée.');
      this.footer.append(warn);
    }
    const why = this.pending
      ? ''
      : (firstShortfall(needs) ?? (this.inReach ? undefined : uiText('Approche-toi du graveur.')));
    const label =
      seals.length > 0
        ? uiFormat('Graver et sceller · {gold}', { gold: formatGold(cost.gold) })
        : uiFormat('Graver · {gold}', { gold: formatGold(cost.gold) });
    this.footer.append(
      workAction(label, why, (button) => {
        this.pending = true;
        button.disabled = true;
        this.options.onReforge(piece.instance.id, { locks, values, seals });
      }),
    );

    if (this.last?.itemId === piece.instance.id) this.detail.append(beforeAfter(this.last));
  }

  private named(choice: Exclude<RuneChoice, 'new'>): AffixId[] {
    return [...this.choices].filter(([, chosen]) => chosen === choice).map(([id]) => id);
  }
}
